import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Person, Email, Phone, FileCopy, Add, Delete, CheckCircle, Send, Visibility, Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { Field } from '@campfire/field';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { unpackToDate, unpackToDateTime } from '@campfire/hot-date';
import { convertFromRaw, EditorState } from 'draft-js';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { useSnackbar } from '../../../../../../../global/config/useSnackbar';
import { useApiUrl } from '../../../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../../../global/network/useCampfireFetch';
import { ConfirmationDialog } from '../../../../../../../common/dialogs/ConfirmationDialog';

const useStyles = makeStyles(() =>
  createStyles({
    progressSection: {
      marginBottom: '16px',
    },
    refereeItem: {
      padding: '12px',
      marginBottom: '8px',
      border: '1px solid #f0f0f0',
      borderRadius: '8px',
      backgroundColor: '#fafafa',
    },
    refereeInfo: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '4px',
    },
    refereeInfoIcon: {
      fontSize: '16px',
      color: '#666',
      marginRight: '8px',
    },
    copyButton: {
      padding: '4px',
      marginLeft: '8px',
    },
    completedReferee: {
      borderLeftColor: '#4caf50',
      borderLeftWidth: '4px',
      backgroundColor: '#fafafa',
    },
    addRefereeForm: {
      backgroundColor: '#f9f9f9',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '16px',
    },
  })
);

interface RefereeSubmission {
  refereeSubmissionId: string;
  refereeName: string;
  refereeEmail: string;
  refereePhone?: string;
  submissionToken: string;
  dateInvited?: string;
  dateLastReminded?: string;
  dateSubmitted?: string;
  isCompleted: boolean;
  submissionData?: string | null;
}

interface RefereeCheckData {
  taskRefereeCheckId: string;
  requiredReferencesCount: number;
  instructions?: string;
  refereeSubmissions: RefereeSubmission[];
  completedSubmissionsCount: number;
  pendingSubmissionsCount: number;
  isComplete: boolean;
  dateCreated: string;
}

interface IncomingVolunteerRefereeCheckViewProps {
  refereeCheck: RefereeCheckData | null;
  taskId: string;
  profileId: string;
  userId: string;
  baseUrl?: string;
  isReadOnly?: boolean;
  onRefresh?: () => void;
  taskItems?: any[]; // Task field definitions for resolving field IDs to names
}

const validationSchema = Yup.object({
  refereeName: Yup.string().required('Referee name is required'),
  refereeEmail: Yup.string()
    .email('Please enter a valid email address')
    .required('Referee email is required'),
  refereePhone: Yup.string().required('Referee phone number is required'),
});

export const IncomingVolunteerRefereeCheckView: React.FC<IncomingVolunteerRefereeCheckViewProps> = ({
  refereeCheck,
  taskId,
  profileId,
  baseUrl = window.location.origin,
  isReadOnly = false,
  onRefresh,
  taskItems = [],
}) => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();

  // Convert rich text instructions to editor state for display
  const instructionsEditorState = useMemo(() => {
    if (!refereeCheck?.instructions) return null;

    try {
      // Try to parse as rich text JSON
      const contentState = convertFromRaw(JSON.parse(refereeCheck.instructions));
      return EditorState.createWithContent(contentState);
    } catch {
      // If it's not JSON, treat as plain text
      return null;
    }
  }, [refereeCheck?.instructions]);

  // Helper function to get task field by ID
  const getTaskField = (fieldId: string) => {
    // Try multiple ways to find the field
    let taskItem = taskItems.find(
      (item) =>
        item.taskItemId === fieldId ||
        item.taskFieldId === fieldId ||
        item.field?.taskFieldId === fieldId ||
        item.field?.id === fieldId ||
        item.id === fieldId
    );

    // If still not found, try to find by any nested field ID
    if (!taskItem) {
      taskItem = taskItems.find((item) => {
        // Check if the field object has any ID that matches
        const { field } = item;
        if (field) {
          return Object.values(field).some((value) => value === fieldId);
        }
        return false;
      });
    }

    if (!taskItem) {
      return null;
    }

    // Extract field information based on the structure
    return {
      taskFieldId: fieldId,
      fieldName: taskItem.field?.name || taskItem.fieldName || `Field ${fieldId.substring(0, 8)}...`,
      fieldType: taskItem.field?.__typename || taskItem.fieldType || 'short',
      helpText: taskItem.field?.description || taskItem.helpText || null,
      isRequired: taskItem.field?.isRequired || taskItem.isRequired || false,
      selectOptions:
        taskItem.field?.multipleChoiceOptions?.map((opt: any) => opt.name) ||
        taskItem.field?.selectOptions ||
        taskItem.selectOptions ||
        null,
    };
  };

  // Helper function to render field value in read-only format
  const renderFieldValue = (field: any, value: any) => {
    if (!value) {
      return (
        <Box
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '12px',
            backgroundColor: '#fafafa',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant='body2' color='textSecondary' style={{ fontStyle: 'italic' }}>
            No response provided
          </Typography>
        </Box>
      );
    }

    // For long text fields, preserve line breaks and use multiline styling
    if (field.fieldType === 'long' || field.fieldType?.includes('LongText')) {
      return (
        <Box
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '12px',
            backgroundColor: '#ffffff',
            minHeight: '100px',
          }}
        >
          <Typography variant='body2' style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {value}
          </Typography>
        </Box>
      );
    }

    // For dropdown or multiple choice - style like a form field
    if (
      field.fieldType === 'dropdown' ||
      field.fieldType === 'multiple_choice' ||
      field.fieldType?.includes('MultipleChoice') ||
      field.fieldType?.includes('Dropdown')
    ) {
      return (
        <Box
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '12px',
            backgroundColor: '#ffffff',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant='body2'>{value}</Typography>
        </Box>
      );
    }

    // Default for short text and other field types
    return (
      <Box
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          padding: '12px',
          backgroundColor: '#ffffff',
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant='body2'>{value}</Typography>
      </Box>
    );
  };
  const [showAddForm, setShowAddForm] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [refereeToRemove, setRefereeToRemove] = useState<{ id: string; name: string; isCompleted: boolean } | null>(
    null
  );
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<RefereeSubmission | null>(null);

  // API hooks
  const addRefereeContactFetch = useCampfireFetch({ defer: true });
  const resendEmailFetch = useCampfireFetch({ defer: true });
  const sendInvitationsFetch = useCampfireFetch({ defer: true });
  const removeRefereeContactFetch = useCampfireFetch({ defer: true });

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({
        open: true,
        message: `${label} copied to clipboard`,
        variant: 'success',
      });
    });
  };

  const generateRefereeUrl = (token: string) => {
    return `${baseUrl}/referee-check/${token}`;
  };

  const handleSendInvitations = async () => {
    try {
      const response = await sendInvitationsFetch.run({
        url: `${apiUrl}/vm/task/referee-check/send-invitations`,
        method: 'post',
        data: {
          taskId,
          profileId,
        },
      });

      if (response.ok && response.data?.data) {
        const responseData = response.data.data as { count?: number };
        const sentCount = responseData.count || 0;
        setSnackbar({
          open: true,
          message:
            (response.data as { message?: string }).message ||
            `Invitations sent to ${sentCount} referee${sentCount !== 1 ? 's' : ''}`,
          variant: 'success',
        });
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error('Failed to send invitations');
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send invitations. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleAddReferee = async (values: { refereeName: string; refereeEmail: string; refereePhone: string }) => {
    try {
      const response = await addRefereeContactFetch.run({
        url: `${apiUrl}/vm/task/referee-contact/add`,
        method: 'post',
        data: {
          taskId,
          profileId,
          refereeName: values.refereeName,
          refereeEmail: values.refereeEmail,
          refereePhone: values.refereePhone,
          // Don't send refereeMessage for admin interface - only volunteers can add personal messages
        },
      });

      if (response.ok && response.data?.data) {
        setShowAddForm(false);
        setSnackbar({
          open: true,
          message: 'Referee contact added successfully',
          variant: 'success',
        });
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error('Failed to add referee contact');
      }
    } catch (error) {
      console.error('Error adding referee contact:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add referee contact. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleResendEmail = async (submissionId: string) => {
    try {
      const response = await resendEmailFetch.run({
        url: `${apiUrl}/vm/task/referee-check/send-reminder`,
        method: 'post',
        data: {
          refereeContactId: submissionId,
        },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Email resent successfully',
          variant: 'success',
        });
      } else {
        throw new Error('Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send reminder. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleRemoveReferee = async (submissionId: string, refereeName: string, isCompleted: boolean) => {
    setRefereeToRemove({ id: submissionId, name: refereeName, isCompleted });
    setRemoveDialogOpen(true);
  };

  const confirmRemoveReferee = async () => {
    if (!refereeToRemove) return;

    try {
      const response = await removeRefereeContactFetch.run({
        url: `${apiUrl}/vm/task/referee-contact/remove`,
        method: 'post',
        data: {
          refereeContactId: refereeToRemove.id,
        },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `${refereeToRemove.name} removed successfully`,
          variant: 'success',
        });
        if (onRefresh) {
          onRefresh();
        }
        setRemoveDialogOpen(false);
        setRefereeToRemove(null);
      } else {
        throw new Error('Failed to remove referee contact');
      }
    } catch (error) {
      console.error('Error removing referee contact:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove referee contact. Please try again.',
        variant: 'error',
      });
      setRemoveDialogOpen(false);
      setRefereeToRemove(null);
    }
  };

  if (!refereeCheck) {
    return (
      <Box textAlign='center' py={4}>
        <Typography variant='body2' color='textSecondary'>
          No referee check data available for this task
        </Typography>
      </Box>
    );
  }

  const progressPercentage =
    refereeCheck.requiredReferencesCount > 0
      ? (refereeCheck.completedSubmissionsCount / refereeCheck.requiredReferencesCount) * 100
      : 0;

  // Calculate invitation status
  const uninvitedReferees = refereeCheck.refereeSubmissions.filter((ref) => !ref.dateInvited);
  const hasUninvitedReferees = uninvitedReferees.length > 0;

  // Check if we've reached the maximum number of referees
  const hasReachedRefereeLimit = refereeCheck.refereeSubmissions.length >= refereeCheck.requiredReferencesCount;

  // A reminder is due 24 hours after the last time a referee was invited or reminded
  const getReminderDueDate = (referee: RefereeSubmission) => {
    const lastContacted = referee.dateLastReminded || referee.dateInvited;
    if (!lastContacted) return null;
    return new Date(
      unpackToDate(lastContacted)
        .toJSDate()
        .getTime() +
        24 * 60 * 60 * 1000
    );
  };

  const canSendReminderTo = (referee: RefereeSubmission) => {
    if (referee.isCompleted) return false;
    const dueDate = getReminderDueDate(referee);
    return dueDate !== null && dueDate <= new Date();
  };

  const getReminderTooltip = (referee: RefereeSubmission) => {
    const dueDate = getReminderDueDate(referee);
    if (dueDate === null) return '';
    if (canSendReminderTo(referee)) return `Send ${referee.refereeName} a reminder to respond`;

    const timeUntilAvailable = dueDate.getTime() - new Date().getTime();
    if (timeUntilAvailable > 0) {
      const hoursUntilAvailable = Math.ceil(timeUntilAvailable / (1000 * 60 * 60));
      return `A reminder can be sent in ${hoursUntilAvailable} hour${hoursUntilAvailable !== 1 ? 's' : ''}`;
    }
    return 'Must wait 24 hours since the last invitation or reminder to send another';
  };

  const getStatusChipProps = (submission: RefereeSubmission) => {
    if (submission.isCompleted) {
      return {
        label: 'Completed',
        style: {
          backgroundColor: '#e8f5e8',
          color: '#2e7d32',
          borderColor: '#2e7d32',
          padding: '4px 12px',
          height: '32px',
          fontWeight: 500,
        },
        icon: <CheckCircle style={{ fontSize: '14px', marginRight: '4px', color: '#2e7d32' }} />,
      };
    }
    if (submission.dateInvited) {
      return {
        label: 'Sent',
        style: {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          borderColor: '#1976d2',
          padding: '4px 12px',
          height: '32px',
          fontWeight: 500,
        },
        icon: <Send style={{ fontSize: '14px', marginRight: '4px', color: '#1976d2' }} />,
      };
    }
    return {
      label: 'Not Sent',
      style: {
        backgroundColor: '#f5f5f5',
        color: '#616161',
        borderColor: '#9e9e9e',
        padding: '4px 12px',
        height: '32px',
        fontWeight: 500,
      },
      icon: undefined,
    };
  };

  return (
    <Box
      marginTop={2}
      marginBottom={3}
      paddingTop={3}
      paddingBottom={3}
      paddingLeft={4}
      paddingRight={4}
      border={'1px solid #e9e9e9'}
      bgcolor='#ffffff'
      borderRadius={7}
    >
      {/* Progress Section */}
      <Box className={classes.progressSection}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
          <Typography variant='body2' style={{ fontWeight: 600 }}>
            Reference Progress
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {refereeCheck.completedSubmissionsCount} of {refereeCheck.requiredReferencesCount} completed
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={progressPercentage}
          style={{
            height: '8px',
            borderRadius: '4px',
            backgroundColor: '#f0f0f0',
          }}
        />
      </Box>

      {/* Instructions */}
      {refereeCheck.instructions && (
        <Box mb={2}>
          <Typography variant='caption' color='textSecondary'>
            Instructions:
          </Typography>
          <Typography variant='body2' style={{ fontSize: '14px', color: '#808080', paddingTop: '5px' }}>
            {instructionsEditorState ? (
              <CampfireRichEditor readOnly editorState={instructionsEditorState} />
            ) : (
              refereeCheck.instructions
            )}
          </Typography>
        </Box>
      )}

      {/* Send Invitations Section */}
      {hasUninvitedReferees && (
        <Box mb={2} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '8px' }}>
            Ready to Send Invitations
          </Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: '12px' }}>
            {uninvitedReferees.length} referee{uninvitedReferees.length > 1 ? 's' : ''} will receive an email with a
            secure link to submit their reference.
          </Typography>
          <TabletButton
            color='primary'
            variant='contained'
            onClick={handleSendInvitations}
            disabled={sendInvitationsFetch.isLoading}
            size='small'
            endIcon={<Email />}
          >
            {sendInvitationsFetch.isLoading
              ? 'Sending...'
              : `Send ${uninvitedReferees.length} Invitation${uninvitedReferees.length > 1 ? 's' : ''}`}
          </TabletButton>
        </Box>
      )}

      {/* Add Referee Button */}
      {!isReadOnly && !showAddForm && (
        <Box mb={2}>
          <TabletButton
            endIcon={<Add />}
            variant='outlined'
            color='primary'
            onClick={() => setShowAddForm(true)}
            size='small'
            fullWidth
            disabled={hasReachedRefereeLimit}
            title={
              hasReachedRefereeLimit ? `Maximum of ${refereeCheck.requiredReferencesCount} referees allowed` : undefined
            }
          >
            Add Referee
          </TabletButton>
        </Box>
      )}

      {/* Add Referee Form */}
      {!isReadOnly && showAddForm && (
        <Box className={classes.addRefereeForm} mb={2}>
          <Typography variant='body1' gutterBottom style={{ fontWeight: 600, fontSize: '0.875rem' }}>
            Add Referee Contact
          </Typography>
          <Formik
            initialValues={{ refereeName: '', refereeEmail: '', refereePhone: '' }}
            validationSchema={validationSchema}
            onSubmit={handleAddReferee}
          >
            {() => (
              <Form>
                <Box mb={3}>
                  <Field
                    name='refereeName'
                    label='Referee Name'
                    placeholder='Full name of the referee'
                    required
                    fullWidth
                    variant='outlined'
                    size='small'
                  />
                </Box>
                <Box mb={3}>
                  <Field
                    name='refereeEmail'
                    label='Referee Email'
                    type='email'
                    placeholder='referee@example.com'
                    required
                    fullWidth
                    variant='outlined'
                    size='small'
                  />
                </Box>
                <Box mb={3}>
                  <Field
                    name='refereePhone'
                    label='Referee Phone Number'
                    placeholder='+1 555 123 4567'
                    required
                    fullWidth
                    variant='outlined'
                    size='small'
                  />
                </Box>
                <Box display='flex' justifyContent='flex-end'>
                  <Button
                    onClick={() => setShowAddForm(false)}
                    size='small'
                    style={{ marginRight: '8px', textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <TabletButton color='primary' variant='contained' type='submit' size='small' endIcon={<Add />}>
                    Add
                  </TabletButton>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      )}

      {/* Referee Submissions - Only show if there are referees */}
      {refereeCheck.refereeSubmissions.length > 0 && (
        <>
          <Typography variant='subtitle2' style={{ fontWeight: 600, marginBottom: '8px' }}>
            Referees ({refereeCheck.refereeSubmissions.length})
          </Typography>

          {refereeCheck.refereeSubmissions.map((submission, index) => (
            <Box key={submission.refereeSubmissionId}>
              {index > 0 && <Divider style={{ margin: '8px 0' }} />}
              <Box className={classes.refereeItem}>
                <Grid container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <Box className={classes.refereeInfo} style={{ justifyContent: 'space-between' }}>
                      <Box display='flex' alignItems='center'>
                        <Person className={classes.refereeInfoIcon} />
                        <Typography variant='body2' style={{ fontWeight: 600 }}>
                          {submission.refereeName}
                        </Typography>
                      </Box>
                      <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
                        <Chip
                          size='small'
                          icon={getStatusChipProps(submission).icon}
                          label={getStatusChipProps(submission).label}
                          style={getStatusChipProps(submission).style}
                        />
                        {submission.isCompleted && (
                          <TabletButton
                            size='small'
                            variant='outlined'
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setResponseDialogOpen(true);
                            }}
                            style={{
                              minWidth: 'auto',
                              padding: '6px 8px',
                              borderColor: '#2e7d32',
                              color: '#2e7d32',
                            }}
                          >
                            <Visibility style={{ fontSize: '18px', color: '#2e7d32' }} />
                          </TabletButton>
                        )}
                        {!isReadOnly && (
                          <TabletButton
                            size='small'
                            variant='outlined'
                            onClick={() =>
                              handleRemoveReferee(
                                submission.refereeSubmissionId,
                                submission.refereeName,
                                submission.isCompleted
                              )
                            }
                            style={{
                              minWidth: 'auto',
                              padding: '6px 8px',
                              borderColor: '#ff5252',
                              color: '#ff5252',
                            }}
                          >
                            <Delete style={{ fontSize: '18px', color: '#ff5252' }} />
                          </TabletButton>
                        )}
                      </Box>
                    </Box>
                    <Box className={classes.refereeInfo}>
                      <Email className={classes.refereeInfoIcon} />
                      <Typography variant='body2'>{submission.refereeEmail}</Typography>
                      <IconButton
                        className={classes.copyButton}
                        onClick={() => copyToClipboard(submission.refereeEmail, 'Email')}
                        size='small'
                      >
                        <FileCopy fontSize='inherit' />
                      </IconButton>
                    </Box>
                    {submission.refereePhone && (
                      <Box className={classes.refereeInfo}>
                        <Phone className={classes.refereeInfoIcon} />
                        <Typography variant='body2'>{submission.refereePhone}</Typography>
                        <IconButton
                          className={classes.copyButton}
                          onClick={() => copyToClipboard(submission.refereePhone!, 'Phone')}
                          size='small'
                        >
                          <FileCopy fontSize='inherit' />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {submission.isCompleted ? (
                      <Box>
                        <Typography variant='caption' color='textSecondary'>
                          Completed:
                        </Typography>
                        <Typography variant='body2'>
                          {unpackToDateTime(submission.dateSubmitted!)
                            .toFormat('h:mm a')
                            .toLowerCase()}
                          ,{' '}
                          {unpackToDateTime(submission.dateSubmitted!).toLocaleString({
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Typography>
                      </Box>
                    ) : submission.dateInvited ? (
                      <Box>
                        <Box mb={1}>
                          <Typography variant='caption' color='textSecondary' display='block'>
                            Invited:{' '}
                            {unpackToDateTime(submission.dateInvited)
                              .toFormat('h:mm a')
                              .toLowerCase()}
                            ,{' '}
                            {unpackToDateTime(submission.dateInvited).toLocaleString({
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </Typography>
                        </Box>
                        <Box display='flex' alignItems='center' mb={1}>
                          <Typography variant='caption' color='textSecondary' style={{ marginRight: '8px' }}>
                            Referee Link:
                          </Typography>
                          <IconButton
                            size='small'
                            onClick={() =>
                              copyToClipboard(generateRefereeUrl(submission.submissionToken), 'Referee link')
                            }
                          >
                            <FileCopy fontSize='inherit' />
                          </IconButton>
                        </Box>
                        {!isReadOnly && (
                          <Tooltip title={getReminderTooltip(submission)} arrow>
                            <span>
                              <TabletButton
                                size='small'
                                variant='contained'
                                color='primary'
                                onClick={() => handleResendEmail(submission.refereeSubmissionId)}
                                disabled={!canSendReminderTo(submission) || resendEmailFetch.isLoading}
                                endIcon={<Send />}
                                style={{
                                  fontSize: '12px',
                                  padding: '6px 12px',
                                  textTransform: 'none',
                                }}
                              >
                                {resendEmailFetch.isLoading ? 'Sending...' : 'Send Reminder'}
                              </TabletButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant='caption' color='textSecondary'>
                          Invitation will be sent when you click &quot;Send Invitations&quot; above
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          ))}
        </>
      )}

      <ConfirmationDialog
        open={removeDialogOpen}
        title='Remove Referee'
        body={
          refereeToRemove
            ? refereeToRemove.isCompleted
              ? `Are you sure you want to remove ${refereeToRemove.name} as a referee? This referee has already completed and submitted their reference check. This action cannot be undone and will permanently delete their submitted response.`
              : `Are you sure you want to remove ${refereeToRemove.name} as a referee? This action cannot be undone.`
            : ''
        }
        closeActionText='Cancel'
        confirmActionText='Remove'
        handleCloseClick={() => setRemoveDialogOpen(false)}
        handleConfirmClick={confirmRemoveReferee}
      />

      {/* Referee Response View Dialog */}
      <Dialog open={responseDialogOpen} onClose={() => setResponseDialogOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6' style={{ fontWeight: 600 }}>
              Referee Response - {selectedSubmission?.refereeName}
            </Typography>
            <IconButton onClick={() => setResponseDialogOpen(false)} size='small' style={{ padding: '4px' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSubmission && (
            <Box>
              <Box mb={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant='body2' color='textSecondary' gutterBottom>
                  <strong>Submitted:</strong>{' '}
                  {unpackToDateTime(selectedSubmission.dateSubmitted!)
                    .toFormat('h:mm a')
                    .toLowerCase()}
                  ,{' '}
                  {unpackToDateTime(selectedSubmission.dateSubmitted!).toLocaleString({
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  <strong>Referee:</strong> {selectedSubmission.refereeName} ({selectedSubmission.refereeEmail})
                </Typography>
              </Box>

              {selectedSubmission.submissionData ? (
                <Box>
                  <Typography variant='h6' gutterBottom style={{ fontWeight: 600, marginBottom: '16px' }}>
                    Response Details
                  </Typography>
                  {(() => {
                    try {
                      const parsedData = JSON.parse(selectedSubmission.submissionData);

                      // Sort fields by their order in taskItems to maintain consistent ordering
                      const sortedEntries = Object.entries(parsedData).sort(([fieldIdA], [fieldIdB]) => {
                        const indexA = taskItems.findIndex(
                          (item) => item.taskItemId === fieldIdA || item.taskFieldId === fieldIdA
                        );
                        const indexB = taskItems.findIndex(
                          (item) => item.taskItemId === fieldIdB || item.taskFieldId === fieldIdB
                        );
                        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                      });

                      return sortedEntries.map(([fieldId, value], index) => {
                        const field = getTaskField(fieldId);

                        if (!field) {
                          // If we can't find the field definition, show raw data
                          return (
                            <Box key={fieldId} mb={3}>
                              {index > 0 && <Divider style={{ margin: '16px 0' }} />}
                              <Typography variant='body2' color='textSecondary'>
                                Unknown field ({fieldId}): {String(value)}
                              </Typography>
                            </Box>
                          );
                        }

                        return (
                          <Box key={fieldId} marginBottom={3}>
                            <Typography
                              variant='body1'
                              style={{
                                fontWeight: 600,
                                marginBottom: 8,
                                color: '#3E3229',
                              }}
                            >
                              {field.fieldName}
                              {field.isRequired && <span style={{ color: '#f44336', marginLeft: 4 }}>*</span>}
                            </Typography>
                            {field.helpText && (
                              <Typography
                                variant='body2'
                                style={{
                                  marginBottom: 8,
                                  color: '#646464',
                                }}
                              >
                                {field.helpText}
                              </Typography>
                            )}
                            {renderFieldValue(field, value)}
                          </Box>
                        );
                      });
                    } catch (error) {
                      return (
                        <Box
                          p={2}
                          style={{ backgroundColor: '#ffebee', borderRadius: '8px', border: '1px solid #ffcdd2' }}
                        >
                          <Typography variant='body2' color='error'>
                            Error parsing submission data. Raw data: {selectedSubmission.submissionData}
                          </Typography>
                        </Box>
                      );
                    }
                  })()}
                </Box>
              ) : (
                <Box p={3} textAlign='center' style={{ backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                  <Typography variant='body2' color='textSecondary' style={{ fontStyle: 'italic' }}>
                    No response data available for this submission.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <TabletButton onClick={() => setResponseDialogOpen(false)} color='primary' variant='contained'>
            Close
          </TabletButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
